<template>
    <v-container fluid>
        <h1 class="sub_tit">방문신청조회</h1>

        <v-card v-if="!searchParams" class="mb-8 pa-6 a10-apply-card">
            <v-form ref="form" v-model="valid" lazy-validation>
                <v-row>
                    <v-col>
                        <v-text-field
                            v-model="name"
                            :rules="rules.name"
                            label="이름 *"
                            maxlength="20"
                            @keypress.enter="search"
                            required
                        ></v-text-field>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col>
                        <v-text-field
                            v-model="mobile"
                            :rules="rules.mobile"
                            @keyup="changeMobile(mobile)"
                            @keypress.enter="search"
                            label="휴대폰 *"
                            maxlength="13"
                            required
                        ></v-text-field>
                    </v-col>
                </v-row>

                <v-card-actions class="justify-center">
                    <v-btn
                        color="#023894"
                        depressed
                        style="color:#fff"
                        :disabled="!valid"
                        @click="search"
                    >
                        조회
                    </v-btn>
                </v-card-actions>

            </v-form>
        </v-card>

        <v-card v-else class="mb-8 pa-6 a10-apply-card">
            <v-toolbar flat height="48">
                <v-toolbar-title v-show="!$vuetify.breakpoint.smAndDown">신청일 기준: </v-toolbar-title>
                <v-btn-toggle
                        dense
                        mandatory
                        v-model="range"
                        background-color="primary"
                        group
                        @change="changeRange"
                        class="mr-4"
                >
                    <v-btn value="-1">
                        최근 1개월
                    </v-btn>

                    <v-btn value="-999">
                        전체
                    </v-btn>
                </v-btn-toggle>

                <v-spacer />

                <v-btn @click="retry" color="#78BE20" style="color:#fff" depressed>
                    다시 조회
                </v-btn>
            </v-toolbar>

            <v-divider class="mb-3" />

            <v-data-table
                class="reserve-list"
                :headers="headers"
                :items="histories"
                :items-per-page="-1"
                item-key="visitId"
                no-data-text="신청한 내역이 없습니다."
                mobile-breakpoint="900"
                hide-default-footer
                :disable-sort="$vuetify.breakpoint.smAndDown"
                @click:row="clickRow"
            >
                <template v-slot:item.visitStatus="{ item }">
                    <v-chip small
                            :color="getStatusInfo(item.visitStatus).backgroundColor"
                            :text-color="getStatusInfo(item.visitStatus).color"
                            class="mr-1"
                    >{{ getStatusInfo(item.visitStatus).text }}
                    </v-chip>
                </template>

                <template v-slot:item.visitUserName="{ item }">
                    {{ item.visitUserName }} {{ item.visitDeptName }}
                </template>

                <template v-slot:item.registDatetime="{ item }">
                    {{ item.registDatetime | calendar }}
                </template>

                <template v-slot:item.startDatetime="{ item }">
                    {{ item.startDatetime | calendar }}
                </template>

                <template v-slot:item.qrcode="{ item }">
                    <v-chip
                            v-if="'APPROVED' === item.visitStatus && item.encryptedVisitId"
                            color="#78BE20"
                            style="color: #fff"
                            :to="`/home/qrcode/${item.encryptedVisitId}`"
                        >
                            <v-icon left small>
                                mdi-qrcode
                            </v-icon>
                            QR 코드
                        </v-chip>
                </template>

                <template v-slot:item.actions="{ item }">
                    <template v-if="['WAITING', 'PENDING', 'APPROVED'].includes(item.visitStatus)">
                        <v-btn fab text class="mr-2" small @click.stop="cancelApply(item)" color="primary">
                            취소
                        </v-btn>
                    </template>
                    <!-- 디버깅용 -->
<!--                    <v-btn icon :to="`/home/visitInfo/t/${item.encryptedVisitId}`">-->
<!--                            <v-icon>mdi-bug</v-icon>-->
<!--                    </v-btn>-->
                </template>
            </v-data-table>
        </v-card>

        <v-dialog
            v-model="dialog.show"
            :fullscreen="$vuetify.breakpoint.smAndDown"
            max-width="900"
            scrollable
        >
            <v-card>
                <v-toolbar color="primary" dark height="60">
                    <v-toolbar-title>방문신청 정보</v-toolbar-title>
                </v-toolbar>

                <template v-if="dialog.show">
                    <v-card-text class="pa-8">
                         <apply-view :visitId="dialog.item.visitId"/>
                    </v-card-text>
                </template>

                <v-footer outlined class="justify-center elevation-2">
                    <v-btn class="secondary-btn"
                            @click="close"
                            width="120"
                    >
                        닫기
                    </v-btn>
                </v-footer>
            </v-card>
        </v-dialog>

    </v-container>
</template>

<script>
import utilService from "@/service/utilService";
import {mapState} from 'vuex';
import ApplyActionMixin from "@/components/apply/ApplyActionMixin";

export default {
    name: 'Search',
    mixins: [ApplyActionMixin],
    components: {
        ApplyView: () => import("@/components/apply/ApplyView"),
    },

    data: () => ({
        valid: false,
        loading: false,
        name: null,
        mobile: null,
        range: '-1',

        headers: [
            {text: '방문신청ID', value: 'visitId', sortable: true},
            {text: '접견자', value: 'visitUserName', sortable: true},
            {text: '방문 일정', value: 'startDatetime', width: 250, sortable: true},
            {text: '신청 날짜', value: 'registDatetime', width: 250, sortable: true},
            {text: 'QR코드', value: 'qrcode', align: 'center', sortable: false},
            {text: '상태', value: 'visitStatus', sortable: true},
            {text: '액션', value: 'actions', align: 'center', sortable: false,},
        ],
        histories: [],

        rules: {
            name: [v => !!v || '이름을 입력하세요'],
            mobile: [
                v => {
                    if (v) {
                        const re = /(01[016789])[-]?(\d{3,4})[-]?(\d{4})$/g;
                        const match = re.exec(v);
                        if (match) {
                            return true;
                        }
                    }

                    return '정확한 휴대폰 번호를 입력하세요';
                }
            ],
        },

        dialog: {
            show: false,
            mode: 'edit',
            item: null,
        }
    }),

    computed: {
        ...mapState(['currentUser', 'secretKey', 'iv']),
        searchParams: {
            get() {
                return this.$store.state.searchParam;
            },
            set(searchParams) {
                this.$store.commit('setSearchParam', searchParams)
            }
        }
    },

    created() {
        if (this.searchParams) {
            this.valid = true;
            this.name = this.searchParams.name;
            this.mobile = this.searchParams.mobile;
            this.range = this.searchParams.range ? this.searchParams.range : '-1';
            this.search();
        } else if (this.currentUser) {
            this.name = this.currentUser.userName;
            this.mobile = this.currentUser.mobile;
            this.range = '-1';
        }
    },

    methods: {
        async changeRange() {
            await this.search();
        },

        async search() {
            if (!this.valid) {
                this.$window.alert('이름과 휴대폰 번호를 확인해주세요');
                return;
            }

            this.$window.showLoading('조회 중...');

            try {
                const res = await this.$axios.get('/v1/home/visit/recentHistory', {
                    params: {
                        name: this.name,
                        mobile: utilService.normalizeMobile(this.mobile),
                        range: this.range
                    }
                })

                this.searchParams = {
                    name: this.name,
                    mobile: this.mobile,
                    range: this.range
                }

                this.histories = res.data;

                this.histories.forEach(history => {
                    history.encryptedVisitId = this.getEncryptedVisitId(history.visitId);
                })

                if (this.histories.length === 0) {
                    this.$window.alert('방문 신청한 내역이 없습니다.');
                }
            } catch (err) {
                console.error(err);
                let msg = err.response.data ? err.response.data: '조회가 실패하였습니다.';
                this.$window.alert(msg, 'error')
            } finally {
                this.$window.hideLoading();
            }
        },

        retry() {
            this.name = this.currentUser ? this.currentUser.userName : null;
            this.mobile = this.currentUser ? this.currentUser.mobile : null;
            this.range = '-1';
            this.searchParams = null;
            this.histories = [];
        },

        changeMobile(str) {
            this.mobile = utilService.humanReadableMobile(str);
        },

        clickRow(item) {
            this.dialog.item = item;
            this.dialog.mode = (['WAITING', 'PENDING'].includes(item.visitStatus)) ? 'edit' : 'view';
            this.dialog.show = true;
        },

        async cancelApply(item) {
            const reason = await this.$window.prompt('취소 사유를 입력해주세요.', true, '취소 사유');

            if (!reason) {
                return;
            }

            this.$window.showLoading("취소 중...");
            try {
                const { visitors } = await this.getApplyInfo(item.visitId);
                await this.cancel(item, visitors, reason);
                await this.search();
            } catch (err) {
                console.error(err);
                this.$window.alert(err.message, 'error');
            } finally {
                this.$window.hideLoading();
            }
        },

        close() {
            this.dialog.show = false;
        },
    }
}
</script>

<style>
.reserve-list tr {
    cursor: pointer;
}
</style>
